import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, BellRing, ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const PROFESSIONI = [
  "Osteopata",
  "Fisioterapista",
  "Medico",
  "Tecnico Ortopedico",
  "Ostetrica",
  "Chinesiologo",
  "Altro professionista sanitario",
];

const steps = ["Anagrafica", "Fatturazione", "Pagamento", "Conferma"];

const inputCls =
  "w-full bg-ivory border border-petrol/15 rounded-xl px-4 py-3 text-sm text-ink placeholder:text-muted2 focus:outline-none focus:border-coral/60 focus:ring-2 focus:ring-coral/15 transition";

const emptyForm = {
  nome: "",
  cognome: "",
  email: "",
  telefono: "",
  professione: "",
  codice_fiscale: "",
  partita_iva: "",
  indirizzo_fatturazione: "",
};

export const Registration = ({ config }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [wl, setWl] = useState({ nome: "", email: "", professione: "" });
  const [wlDone, setWlDone] = useState(false);
  const [wlLoading, setWlLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validStep = () => {
    if (step === 0)
      return form.nome.trim() && form.cognome.trim() && /.+@.+\..+/.test(form.email) && form.telefono.trim().length >= 5 && form.professione;
    if (step === 1) return form.codice_fiscale.trim().length >= 11;
    return true;
  };

  const next = () => {
    if (!validStep()) {
      toast.error("Controlla i campi obbligatori prima di continuare");
      return;
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/registrations", { ...form, metodo_pagamento: "sumup" });
      setResult(data);
      setStep(3);
      toast.success("Iscrizione registrata con successo");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore durante l'iscrizione. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const joinWaitlist = async () => {
    if (!wl.nome.trim() || !/.+@.+\..+/.test(wl.email)) {
      toast.error("Inserisci nome ed email validi");
      return;
    }
    setWlLoading(true);
    try {
      await api.post("/waitlist", wl);
      setWlDone(true);
      toast.success("Sei in lista d'attesa");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore. Riprova.");
    } finally {
      setWlLoading(false);
    }
  };

  const soldOut = config && config.posti_rimanenti === 0;

  return (
    <section id="iscrizione" className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16 bg-petroldeep relative overflow-hidden" data-testid="registration-section">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-coral/10 blur-3xl" />
      <div className="max-w-3xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-coral mb-4">Iscrizione</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium text-ivory tracking-tight">
            Riserva il tuo posto al workshop
          </h2>
          <p className="mt-4 text-ivory/70 text-sm sm:text-base">
            14–15 Novembre 2026 · Taranto — Quota: <span className="font-semibold text-ivory">244,00€</span> (200€ + IVA 22%)
          </p>
          {config && !soldOut && (
            <p
              data-testid="seats-counter"
              className={`mt-4 inline-block font-mono text-xs uppercase tracking-widest px-4 py-2 rounded-full border ${
                config.posti_rimanenti <= 3
                  ? "text-coral border-coral/50 animate-pulse"
                  : "text-ivory/70 border-ivory/20"
              }`}
            >
              {config.posti_rimanenti} posti rimasti su {config.posti_totali}
            </p>
          )}
        </motion.div>

        {!soldOut && (
          <div className="flex items-center justify-center gap-2 mb-10" data-testid="step-indicator">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono transition-colors duration-300 ${i === step ? "bg-coral text-ivory" : i < step ? "bg-ivory/15 text-ivory" : "text-ivory/40"}`}>
                  <span>{String(i + 1).padStart(2, "0")}</span>
                  <span className="hidden sm:inline">{s}</span>
                </div>
                {i < steps.length - 1 && <div className="w-4 h-px bg-ivory/20" />}
              </div>
            ))}
          </div>
        )}

        <div className="bg-ivory rounded-3xl p-7 sm:p-10 shadow-2xl shadow-black/30">
          {soldOut ? (
            wlDone ? (
              <div className="text-center py-8" data-testid="waitlist-success">
                <CheckCircle2 className="w-12 h-12 text-coral mx-auto mb-4" strokeWidth={1.5} />
                <p className="font-serif text-2xl text-petrol font-medium mb-3">Sei in lista d'attesa</p>
                <p className="text-sm text-slate2 max-w-md mx-auto">
                  Ti contatteremo appena si libera un posto o quando apriremo le iscrizioni alla prossima edizione.
                </p>
              </div>
            ) : (
              <div data-testid="waitlist-form">
                <div className="text-center mb-6">
                  <BellRing className="w-10 h-10 text-coral mx-auto mb-3" strokeWidth={1.5} />
                  <p className="font-serif text-2xl text-petrol font-medium mb-2" data-testid="sold-out-message">Posti esauriti</p>
                  <p className="text-sm text-slate2 max-w-md mx-auto">
                    Questa edizione ha raggiunto il limite di {config.posti_totali} partecipanti.
                    Lascia i tuoi contatti: ti avviseremo se si libera un posto o per la prossima edizione.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="waitlist-nome" className={inputCls} placeholder="Nome e Cognome *" value={wl.nome} onChange={(e) => setWl((w) => ({ ...w, nome: e.target.value }))} />
                  <input data-testid="waitlist-email" type="email" className={inputCls} placeholder="Email *" value={wl.email} onChange={(e) => setWl((w) => ({ ...w, email: e.target.value }))} />
                  <select data-testid="waitlist-professione" className={`${inputCls} sm:col-span-2`} value={wl.professione} onChange={(e) => setWl((w) => ({ ...w, professione: e.target.value }))}>
                    <option value="">Professione (opzionale)</option>
                    {PROFESSIONI.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="mt-7 text-center">
                  <button
                    type="button"
                    onClick={joinWaitlist}
                    disabled={wlLoading}
                    data-testid="waitlist-submit-button"
                    className="inline-flex items-center gap-2 bg-petrol hover:bg-petroldeep text-ivory font-semibold text-sm px-7 py-3.5 rounded-full transition-colors duration-300 disabled:opacity-50"
                  >
                    {wlLoading ? "Invio in corso..." : "Avvisami"} <BellRing className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          ) : (
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="input-nome" className={inputCls} placeholder="Nome *" value={form.nome} onChange={set("nome")} />
                  <input data-testid="input-cognome" className={inputCls} placeholder="Cognome *" value={form.cognome} onChange={set("cognome")} />
                  <input data-testid="input-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
                  <input data-testid="input-telefono" className={inputCls} placeholder="Telefono *" value={form.telefono} onChange={set("telefono")} />
                  <select data-testid="input-professione" className={`${inputCls} sm:col-span-2`} value={form.professione} onChange={set("professione")}>
                    <option value="">Professione *</option>
                    {PROFESSIONI.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <StepNav onNext={next} nextLabel="Continua" />
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="input-codice-fiscale" className={inputCls} placeholder="Codice Fiscale *" value={form.codice_fiscale} onChange={set("codice_fiscale")} maxLength={16} />
                  <input data-testid="input-partita-iva" className={inputCls} placeholder="Partita IVA (se applicabile)" value={form.partita_iva} onChange={set("partita_iva")} maxLength={11} />
                  <input data-testid="input-indirizzo-fatturazione" className={`${inputCls} sm:col-span-2`} placeholder="Indirizzo di fatturazione (via, numero, città, CAP)" value={form.indirizzo_fatturazione} onChange={set("indirizzo_fatturazione")} />
                </div>
                <p className="mt-3 text-xs text-muted2">I dati di fatturazione verranno utilizzati per l'emissione della fattura/ricevuta del corso.</p>
                <StepNav onBack={() => setStep(0)} onNext={next} nextLabel="Continua" />
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                <div className="border border-coral/40 bg-coral/5 rounded-2xl p-6 text-center" data-testid="payment-sumup-card">
                  <CreditCard className="w-8 h-8 text-coral mx-auto mb-3" />
                  <p className="font-semibold text-petrol text-sm mb-1">Pagamento sicuro con carta</p>
                  <p className="text-xs text-slate2 leading-relaxed max-w-sm mx-auto">
                    Dopo la conferma potrai completare il pagamento di <strong>244,00€</strong> tramite{" "}
                    <strong>SumUp</strong> (carte, Apple Pay, Google Pay). Troverai il link anche nell'email di conferma.
                  </p>
                </div>
                <StepNav onBack={() => setStep(1)} onNext={submit} nextLabel={submitting ? "Invio in corso..." : "Conferma iscrizione"} disabled={submitting} primaryTestId="registration-submit-button" />
              </motion.div>
            )}

            {step === 3 && result && (
              <motion.div key="s3" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.45 }} className="text-center" data-testid="registration-confirmation">
                <CheckCircle2 className="w-14 h-14 text-coral mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-petrol font-medium mb-2">Iscrizione registrata</h3>
                <p className="text-sm text-slate2 mb-1">Grazie {result.nome}, la tua richiesta è stata salvata.</p>
                <p className="text-sm text-slate2 mb-2">
                  Codice iscrizione:{" "}
                  <span className="font-mono font-semibold text-coral" data-testid="registration-code">{result.codice}</span>
                </p>
                <p className="text-xs text-muted2 mb-6">
                  Ti abbiamo inviato un'email di conferma con riepilogo, mappa e indicazioni per raggiungere la sede.
                </p>
                {config?.sumup_link && (
                  <a
                    href={config.sumup_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="payment-sumup-link"
                    className="inline-flex items-center gap-2 bg-coral hover:bg-coralhover text-ivory font-semibold px-7 py-4 rounded-full transition-colors duration-300 shadow-lg shadow-coral/25"
                  >
                    <CreditCard className="w-5 h-5" /> Completa il pagamento su SumUp · 244€
                  </a>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          )}
        </div>
      </div>
    </section>
  );
};

const StepNav = ({ onBack, onNext, nextLabel, disabled, primaryTestId }) => (
  <div className="mt-7 flex items-center justify-between">
    {onBack ? (
      <button type="button" onClick={onBack} data-testid="step-back-button" className="inline-flex items-center gap-2 text-sm text-slate2 hover:text-petrol transition-colors">
        <ArrowLeft className="w-4 h-4" /> Indietro
      </button>
    ) : <span />}
    <button
      type="button"
      onClick={onNext}
      disabled={disabled}
      data-testid={primaryTestId || "step-next-button"}
      className="inline-flex items-center gap-2 bg-petrol hover:bg-petroldeep text-ivory font-semibold text-sm px-6 py-3.5 rounded-full transition-colors duration-300 disabled:opacity-50"
    >
      {nextLabel} <ArrowRight className="w-4 h-4" />
    </button>
  </div>
);
